import merge from 'lodash/merge';
import { DataSecurityMgr } from '../DataSecurityMgr';
/**
 * @class IVMgr
 * IVMgr is the data security layer encapsulating the Iovation library implementation.
 * The purpose of the class is to configure and load the Iovation library with the configuration provided.
 * The library will generate a unique ID for the device it is used on and pass this class will be able to share that with the mini-app
 * The mini-app needs to add this data as required in the body of its API calls to the downstream systems,
 *
 * @example
 * To set this up in a shell you'll need to know the Library URL,
 * So that when the AppLoader is loading and setting up the mini-app it is able to create the class and set the script tag up on the dom.
 *
 * **NOTE** The Shell only needs to follow the example below, as this initialisation happens within the AppLoader By default
 *
 * ```typescript
 * const iv = new IVMgr("URL TO THE LIBRARY");
 * // this return the Iovation string now.
 * const iovationString: string = await iv.getData();
 * ```
 *
 * @example
 * Shell setup for data Security libraries, usage inside the AppLoader.load
 * To enable this Library in your application.
 *
 * ```typescript
 * AppLoader.load({
 *   appMetadata: {
 *     ...
 *     properties: {
 *       ...
 *       dataSecurity: {
 *         ...
 *         iv: {
 *           LibraryUrl: 'https://www.test.com/libraryUrlForIV'
 *         }
 *       }
 *     }
 *   }
 * });
 * ```
 * @example
 * Mini-app(s) can use this objects passed to them as `dataSecurity` property to App.tsx
 * Irrespective of whether the library is passed or not, the class for IV will be passed down as `dataSecurity.iv`
 *
 * Mini-app(s) can still use `await dataSecurity.iv.getData()`, to see if the promise resolution is positive or failure and handle their case accordingly
 *
 * ```typescript
 * const App = function({ ..., dataSecurity}) {
 *   ...
 *   let data = '';
 *   try{
 *      data  = await dataSecurity.iv.getData();  // returns the IV device value
 *   }
 *   catch(e) {
 *      warn('unable to get the data', e);
 *   }
 *
 *  // using the data.
 *   fetch({
 *     url: 'https://downstream.system.com/server/to/call',
 *     body: {
 *       ...
 *       ...(data && { 'nab-custom-device-id': data })
 *     }
 *   })
 *   ...
 * }
 * ```
 */
class IVMgr extends DataSecurityMgr {
    constructor(libraryUri, config) {
        super(libraryUri, config);
        this.DEVICE_ID_IGL00_VAR_NAME = 'io_global_object_name';
        this.defaultOptions = {
            IGL00_NAME: 'NAB_X_IGL001',
            loader: {
                uri_hook: 'iojs',
                version: '5.0.0'
            }
        };
        this.currentConfig = null;
        this.initialise();
    }
    ivConfigAdd() {
        if (!this.currentConfig) {
            this.currentConfig = merge({}, this.defaultOptions, this.config || {});
        }
        return this.currentConfig;
    }
    async initialise() {
        const { IGL00_NAME, ...loaderConfig } = this.ivConfigAdd();
        window[this.DEVICE_ID_IGL00_VAR_NAME] = IGL00_NAME;
        // tslint:disable-next-lines: variable-name
        window[IGL00_NAME] = window[IGL00_NAME] || { ...loaderConfig };
        return this.loadLibrary();
    }
    async getData() {
        const { IGL00_NAME } = this.ivConfigAdd();
        // if window object exists
        if (window[IGL00_NAME] && window[IGL00_NAME].getBlackbox) {
            const snareData = window[IGL00_NAME].getBlackbox();
            const { blackbox = '' } = snareData || {};
            if (blackbox) {
                return blackbox;
            }
            return Promise.reject(new Error(`Failed reading IV deviceID.`));
        }
        return Promise.reject(new Error('Data security feature for IV not initialised.'));
    }
}
export { IVMgr };
//# sourceMappingURL=IVMgr.js.map